import { rgba } from "polished";
import React, { FC, useRef } from "react";

import styled, { css } from "styled-components";
import { useEditMode } from "../../contexts/edit";

const baseAvatarStyles = css`
  flex-shrink: 0;
  width: ${({ theme }) => theme.sizes.avatarSize};
  height: ${({ theme }) => theme.sizes.avatarSize};
  border-radius: 50%;
  object-fit: cover; // Only for AvatarImg

  @media (max-width: ${({ theme }) => `${theme.breakpoints.tablet}px`}) {
    width: ${({ theme }) => theme.sizes.avatarSizeMobile};
    height: ${({ theme }) => theme.sizes.avatarSizeMobile};
  }

  &.small {
    width: ${({ theme }) => theme.sizes.avatarSmallSize};
    height: ${({ theme }) => theme.sizes.avatarSmallSize};

    @media (max-width: ${({ theme }) => `${theme.breakpoints.tablet}px`}) {
      width: ${({ theme }) => theme.sizes.avatarSmallSize};
      height: ${({ theme }) => theme.sizes.avatarSmallSize};
    }
  }
`;

const AvatarImg = styled.img`
  ${baseAvatarStyles}
`;

const AvatarPlaceholder = styled.div`
  ${baseAvatarStyles}
  border: 1px solid ${({ theme }) => rgba(theme.colors.primary, 0.2)};
  background: ${({ theme }) => rgba(theme.colors.primary, 0.05)};
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 32px;

  @media (max-width: ${({ theme }) => `${theme.breakpoints.tablet}px`}) {
    width: ${({ theme }) => theme.sizes.avatarSizeMobile};
    height: ${({ theme }) => theme.sizes.avatarSizeMobile};
  }

  > h6 {
    text-align: center;
    font-size: 32px;
    font-weight: 800;
    line-height: 100%; /* 32px */
    letter-spacing: -1px;
    color: ${({ theme }) => theme.colors.primary};
    user-select: none;
    margin: 0;
  }

  &.small {
    width: ${({ theme }) => theme.sizes.avatarSmallSize};
    height: ${({ theme }) => theme.sizes.avatarSmallSize};

    > h6 {
      font-size: 14px;
      font-weight: 600;
    }

    @media (max-width: ${({ theme }) => `${theme.breakpoints.tablet}px`}) {
      width: ${({ theme }) => theme.sizes.avatarSmallSize};
      height: ${({ theme }) => theme.sizes.avatarSmallSize};
    }
  }
`;

interface AvatarProps extends Omit<JSX.IntrinsicElements["img"], "ref"> {
  className?: string;
  src?: string;
  alt?: string;
  title: string;
  isSmall?: boolean;
}

export const Avatar: FC<AvatarProps> = ({
  className,
  src,
  alt,
  title,
  isSmall,
}) => {
  return src ? (
    <AvatarImg
      className={`${className || ""} ${isSmall ? "small" : ""}`}
      src={src}
      alt={`${alt} avatar`}
    />
  ) : (
    <AvatarPlaceholder
      className={`${className || ""} ${isSmall ? "small" : ""}`}
    >
      <h6>{getInitials(title)}</h6>
    </AvatarPlaceholder>
  );
};

export const EditableAvatar: FC<AvatarProps> = ({
  className,
  src,
  alt,
  title,
  isSmall,
}) => {
  const {
    editHandlers: { onAvatarChange },
  } = useEditMode();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (file: File | null) => {
    if (!file?.type.startsWith("image")) return;
    onAvatarChange(file);
  };

  return (
    <>
      {src ? (
        <AvatarImg
          style={{ cursor: "pointer" }}
          role="button"
          onClick={() => fileInputRef.current?.click()}
          className={`${className || ""} ${isSmall ? "small" : ""}`}
          src={src}
          alt={`${alt} avatar`}
        />
      ) : (
        <AvatarPlaceholder
          style={{ cursor: "pointer" }}
          role="button"
          onClick={() => fileInputRef.current?.click()}
          className={`${className || ""} ${isSmall ? "small" : ""}`}
        >
          <h6>{getInitials(title)}</h6>
        </AvatarPlaceholder>
      )}
      <input
        ref={fileInputRef}
        style={{ display: "none" }}
        type="file"
        className="file"
        accept="image/*"
        max="1"
        onChange={(e) =>
          e.target.files ? handleFileChange(e.target.files.item(0)) : null
        }
      />
    </>
  );
};

const getInitials = (title: string): string => {
  const words = title.split(" ");
  const firstLetters = words.map((word) => word.charAt(0));
  return firstLetters.slice(0, 3).join("");
};
